import { useEffect, useRef, useState } from "react";
import { llmApi } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loader2, Search } from "lucide-react";

// BC-012: search popular models and pick a route (direct provider or OpenRouter).
// Prices are USD per 1M tokens (input / output) and are only a guide.

const TAG_STYLES = {
  recommended: "bg-emerald-50 text-emerald-700 border-emerald-200",
  popular: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cheap: "bg-amber-50 text-amber-700 border-amber-200",
  free: "bg-slate-50 text-slate-700 border-slate-200",
};
const TAG_LABELS = { recommended: "Recommended", popular: "Popular", cheap: "Low cost", free: "Free tier" };

function fmtPrice(v) {
  if (v === null || v === undefined) return "?";
  if (v === 0) return "0";
  return v < 1 ? v.toFixed(v < 0.1 ? 3 : 2) : v.toFixed(2);
}

function fmtContext(c) {
  if (!c) return "";
  if (c >= 1000000) return `${Math.round(c / 100000) / 10}M`;
  return `${Math.round(c / 1000)}K`;
}

export default function ModelPicker({ currentProvider, currentModel, onPick }) {
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ models: [], providers: [], live: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reqId = useRef(0);

  useEffect(() => {
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await llmApi.catalog(query.trim());
        if (id === reqId.current) setData(res.data || { models: [], providers: [] });
      } catch (e) {
        if (id === reqId.current) setError("Could not load the model list. Try again shortly.");
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, query ? 350 : 0);
    return () => clearTimeout(t);
  }, [query]);

  const labelOf = (pid) => (data.providers || []).find((p) => p.id === pid)?.label || pid;
  const isCurrent = (provider, model) => provider === currentProvider && model === currentModel;

  return (
    <div className="space-y-3 border border-border p-4" data-testid="model-picker">
      <div>
        <p className="text-sm font-medium">Find a model</p>
        <p className="text-xs text-muted-foreground">
          Search popular models and choose how to connect: directly with the provider's own API key, or through OpenRouter with one key for all models.
        </p>
      </div>
      {currentProvider && currentModel && (
        <p className="text-xs border border-border bg-muted/40 p-2 break-all" data-testid="model-selected">
          <span className="font-medium">Selected:</span> {labelOf(currentProvider)} · <span className="font-mono">{currentModel}</span>
        </p>
      )}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value.slice(0, 80))}
          placeholder="Search e.g. qwen, deepseek, gpt, claude, gemini"
          className="rounded-none pl-8"
          data-testid="model-search-input"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {!loading && !error && data.models?.length === 0 && (
        <p className="text-xs text-muted-foreground">No models match "{query}".</p>
      )}
      <div className="max-h-80 overflow-y-auto divide-y divide-border border border-border">
        {(data.models || []).map((m) => (
          <div key={m.id} className="p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid={`model-row-${m.id}`}>
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-sm font-medium break-words">{m.name}</span>
                <span className="text-xs text-muted-foreground">{m.vendor}</span>
                {(m.tags || []).map((t) => (
                  <span key={t} className={`text-[10px] px-1.5 py-0.5 border ${TAG_STYLES[t] || ""}`}>{TAG_LABELS[t] || t}</span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground break-all">
                <span className="font-medium">OpenRouter ID:</span> <span className="font-mono">{m.openrouter}</span>
              </p>
              {m.direct && (
                <p className="text-xs text-muted-foreground break-all">
                  <span className="font-medium">Direct ID ({labelOf(m.direct.provider)}):</span> <span className="font-mono">{m.direct.model}</span>
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Input ${fmtPrice(m.price_in)} · Output ${fmtPrice(m.price_out)} per 1M tokens{fmtContext(m.context) && ` · Context ${fmtContext(m.context)} tokens`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:shrink-0">
              {m.direct && (
                <Button
                  type="button"
                  size="sm"
                  variant={isCurrent(m.direct.provider, m.direct.model) ? "default" : "outline"}
                  className="rounded-none h-7 text-xs"
                  onClick={() => onPick({ provider: m.direct.provider, model: m.direct.model })}
                  data-testid={`pick-direct-${m.id}`}
                  title={`Connect directly to ${labelOf(m.direct.provider)}`}
                >
                  Direct: {labelOf(m.direct.provider).split(" (")[0]}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant={isCurrent("openrouter", m.openrouter) ? "default" : "outline"}
                className="rounded-none h-7 text-xs"
                onClick={() => onPick({ provider: "openrouter", model: m.openrouter })}
                data-testid={`pick-openrouter-${m.id}`}
              >
                Via OpenRouter
              </Button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Prices in USD per 1 million tokens (input / output), as a guide only{data.prices_as_of ? ` (reference ${data.prices_as_of})` : ""}. Check the provider's pricing page before relying on them.
        {data.live === false && " Live OpenRouter prices are unavailable right now, so reference prices are shown."}
      </p>
    </div>
  );
}
